/**
 * PassMapPitch — per-player pass map (Opta "Passes" style): an arrow per
 * completed/incomplete pass with a logged destination, a start-point marker
 * for passes that only have a start location, and four accuracy stats
 * (total/successful/unsuccessful/accuracy).
 *
 * In-app cockpit analytics only — never rendered on the public share page
 * (callers gate this behind the same `hidePlayerHeatmaps` flag used by
 * `HeatMapSection`/`MatchAnalytics`).
 *
 * Client wave 2026-09-19 (EPIC-61, brief 6/AC12 — PMA-COCK-214): the four
 * stats moved from a full-width bar BELOW the pitch to a narrow column
 * BESIDE it (`sm:` and up), part of the "compact ficha" the client asked
 * for on `PlayerCard.tsx`'s player-detail view. AC12's own wording says
 * "public" but there is no public per-player detail to compact (grepped:
 * `PublicMatchAnalytics.tsx` hardcodes `hidePlayerHeatmaps` true and never
 * mounts this component) — a drafting error the lead confirmed and
 * corrected in the spec; this stays cockpit-only, `hidePlayerHeatmaps`
 * untouched.
 *
 * Client wave 2026-09-23 (EPIC-94, item 4/AC6 — dated reversal of the layout
 * immediately above): "pass map top-right with the four KPIs... as a small
 * strip beside the 'MAPA DE PASES' title" — part of `PlayerCard.tsx`'s own
 * further compaction, see that file's doc comment. The four stats move
 * again, from the narrow column beside the pitch to a small inline strip
 * sharing the header row with this component's own `title`; the "big KPI
 * tiles" (`text-lg` boxed values) are gone, replaced by small inline
 * number+label pairs. The pitch itself is now full width below that header
 * row (no more `sm:flex-row` split). Every stat testid (`-stats`,
 * `-stat-total`, `-stat-successful`, `-stat-unsuccessful`, `-stat-accuracy`)
 * is unchanged.
 */
import React, { useId } from "react";
import { PITCH_WIDTH, PITCH_HEIGHT } from "../../utils/heatMapZones";
import {
  PENALTY_ARC_Y_OFFSET,
  PITCH_MARKINGS,
  PITCH_MARKINGS_DERIVED,
} from "../../utils/pitchGeometry";
import type { PassMapData } from "../../utils/passMap";
import { ArrowRight } from "../../../../components/icons";

/* ------------------------------------------------------------------ */
/*  Pitch markup constants (StatsBomb coordinate units)                */
/* ------------------------------------------------------------------ */

const PA_WIDTH = PITCH_MARKINGS.penaltyAreaDepth;
const PA_HEIGHT = PITCH_MARKINGS.penaltyAreaHeight;
const PA_Y = PITCH_MARKINGS_DERIVED.penaltyAreaY;

const GA_WIDTH = PITCH_MARKINGS.goalAreaDepth;
const GA_HEIGHT = PITCH_MARKINGS.goalAreaHeight;
const GA_Y = PITCH_MARKINGS_DERIVED.goalAreaY;

const CC_RX = PITCH_MARKINGS.centerCircleRx;
const CC_RY = PITCH_MARKINGS.centerCircleRy;
const PEN_X = PITCH_MARKINGS.penaltyMarkX;
const PEN_R = PITCH_MARKINGS.penaltyMarkRadius;
const CORNER_R = PITCH_MARKINGS.cornerArcR;

const SUCCESS_COLOR = "#10b981"; // emerald-500 — matches the "Good Passes" stat badge
const FAIL_COLOR = "#f43f5e"; // rose-500 — matches the "Bad Passes" stat badge

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

interface PassMapPitchProps {
  data: PassMapData;
  title?: string;
  t: (key: string, fallback?: string) => string;
  "data-testid"?: string;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const PassMapPitch: React.FC<PassMapPitchProps> = ({
  data,
  title,
  t,
  "data-testid": testId,
}) => {
  const uid = useId().replace(/[:]/g, "");
  const successMarkerId = `${uid}-arrow-success`;
  const failMarkerId = `${uid}-arrow-fail`;

  const isEmpty = data.total === 0;

  return (
    <div
      className="flex flex-col items-center gap-2 w-full"
      data-testid={testId}
    >
      {/* EPIC-94, item 4/AC6: the four stats now share the header row with
          the title as a small inline strip ("beside the 'MAPA DE PASES'
          title"), reversing PMA-COCK-214's stats-beside-the-pitch column
          (removed below) — no more big boxed tiles, no more
          `sm:flex-row` pitch/stats split. Always rendered (even without a
          `title`) so the stat testids/values stay available exactly as
          before. */}
      <div className="flex flex-col items-center justify-center gap-y-2 w-full text-center">
        {title && (
          <span className="text-xs font-semibold text-slate-300 uppercase tracking-wider">
            {title}
          </span>
        )}
        <div
          className="flex w-full flex-wrap items-start justify-center gap-x-6 gap-y-2 text-center text-xs text-slate-200"
          data-testid={testId ? `${testId}-stats` : undefined}
        >
          <span className="flex flex-col items-center gap-0.5">
            <span
              className="text-2xl font-bold text-white"
              data-testid={testId ? `${testId}-stat-total` : undefined}
            >
              {data.total}
            </span>
            <span
              className="text-xs uppercase tracking-wide text-slate-200"
              data-testid={testId ? `${testId}-label-total` : undefined}
            >
              {t("analytics.passMapTotal", "Total Passes")}
            </span>
          </span>
          <span className="flex flex-col items-center gap-0.5 text-emerald-300">
            <span
              className="text-2xl font-bold"
              data-testid={testId ? `${testId}-stat-successful` : undefined}
            >
              {data.successful}
            </span>
            <span className="text-xs uppercase tracking-wide">
              {t("analytics.passMapSuccessful", "Successful")}
            </span>
          </span>
          <span className="flex flex-col items-center gap-0.5 text-rose-300">
            <span
              className="text-2xl font-bold"
              data-testid={testId ? `${testId}-stat-unsuccessful` : undefined}
            >
              {data.unsuccessful}
            </span>
            <span className="text-xs uppercase tracking-wide">
              {t("analytics.passMapUnsuccessful", "Unsuccessful")}
            </span>
          </span>
          <span className="flex flex-col items-center gap-0.5">
            <span
              className="text-2xl font-bold text-white"
              data-testid={testId ? `${testId}-stat-accuracy` : undefined}
            >
              {data.accuracy}%
            </span>
            <span
              className="text-xs uppercase tracking-wide text-slate-200"
              data-testid={testId ? `${testId}-label-accuracy` : undefined}
            >
              {t("analytics.passMapAccuracy", "Accuracy")}
            </span>
          </span>
        </div>
      </div>

      {/* Pitch — full width below the header row (EPIC-94, item 4/AC6: no
          longer split `sm:flex-row` with the stats block, since the stats
          moved into the header row above). */}
      <div className="flex flex-col items-center gap-2 w-full">
        <div
          className="relative w-full max-w-md rounded-lg border border-slate-700 bg-green-800 overflow-hidden"
          style={{ aspectRatio: `${PITCH_WIDTH + 4} / ${PITCH_HEIGHT + 4}` }}
        >
          <svg
            viewBox={`-2 -2 ${PITCH_WIDTH + 4} ${PITCH_HEIGHT + 4}`}
            className="absolute inset-0 w-full h-full"
            preserveAspectRatio="xMidYMid meet"
            role="img"
            aria-label={title ? `${title} pass map` : "Pass map"}
            data-testid={testId ? `${testId}-svg` : undefined}
          >
            <title>{title ? `${title} pass map` : "Pass map"}</title>

            <defs>
              <marker
                id={successMarkerId}
                viewBox="0 0 10 10"
                refX="8"
                refY="5"
                markerWidth="5"
                markerHeight="5"
                orient="auto-start-reverse"
              >
                <path d="M0,0 L10,5 L0,10 z" fill={SUCCESS_COLOR} />
              </marker>
              <marker
                id={failMarkerId}
                viewBox="0 0 10 10"
                refX="8"
                refY="5"
                markerWidth="5"
                markerHeight="5"
                orient="auto-start-reverse"
              >
                <path d="M0,0 L10,5 L0,10 z" fill={FAIL_COLOR} />
              </marker>
            </defs>

            {/* Field background */}
            <rect
              x={0}
              y={0}
              width={PITCH_WIDTH}
              height={PITCH_HEIGHT}
              fill="#2d6a2e"
              rx={1}
            />

            {/* Pitch markings */}
            <g stroke="rgba(255,255,255,0.55)" strokeWidth={0.4} fill="none">
              <rect
                x={0}
                y={0}
                width={PITCH_WIDTH}
                height={PITCH_HEIGHT}
                rx={0.5}
              />
              <line
                x1={PITCH_WIDTH / 2}
                y1={0}
                x2={PITCH_WIDTH / 2}
                y2={PITCH_HEIGHT}
              />
              <ellipse
                cx={PITCH_WIDTH / 2}
                cy={PITCH_HEIGHT / 2}
                rx={CC_RX}
                ry={CC_RY}
              />
              <circle
                cx={PITCH_WIDTH / 2}
                cy={PITCH_HEIGHT / 2}
                r={PEN_R}
                fill="rgba(255,255,255,0.55)"
              />
              <rect x={0} y={PA_Y} width={PA_WIDTH} height={PA_HEIGHT} />
              <rect
                x={PITCH_WIDTH - PA_WIDTH}
                y={PA_Y}
                width={PA_WIDTH}
                height={PA_HEIGHT}
              />
              <rect x={0} y={GA_Y} width={GA_WIDTH} height={GA_HEIGHT} />
              <rect
                x={PITCH_WIDTH - GA_WIDTH}
                y={GA_Y}
                width={GA_WIDTH}
                height={GA_HEIGHT}
              />
              <circle
                cx={PEN_X}
                cy={PITCH_HEIGHT / 2}
                r={PEN_R}
                fill="rgba(255,255,255,0.55)"
              />
              <circle
                cx={PITCH_WIDTH - PEN_X}
                cy={PITCH_HEIGHT / 2}
                r={PEN_R}
                fill="rgba(255,255,255,0.55)"
              />
              <path
                d={`M ${PA_WIDTH} ${
                  PITCH_HEIGHT / 2 - PENALTY_ARC_Y_OFFSET
                } A ${CC_RX} ${CC_RY} 0 0 1 ${PA_WIDTH} ${
                  PITCH_HEIGHT / 2 + PENALTY_ARC_Y_OFFSET
                }`}
              />
              <path
                d={`M ${PITCH_WIDTH - PA_WIDTH} ${
                  PITCH_HEIGHT / 2 - PENALTY_ARC_Y_OFFSET
                } A ${CC_RX} ${CC_RY} 0 0 0 ${PITCH_WIDTH - PA_WIDTH} ${
                  PITCH_HEIGHT / 2 + PENALTY_ARC_Y_OFFSET
                }`}
              />
              <path
                d={`M 0 ${CORNER_R} A ${CORNER_R} ${CORNER_R} 0 0 1 ${CORNER_R} 0`}
              />
              <path
                d={`M ${
                  PITCH_WIDTH - CORNER_R
                } 0 A ${CORNER_R} ${CORNER_R} 0 0 1 ${PITCH_WIDTH} ${CORNER_R}`}
              />
              <path
                d={`M 0 ${
                  PITCH_HEIGHT - CORNER_R
                } A ${CORNER_R} ${CORNER_R} 0 0 0 ${CORNER_R} ${PITCH_HEIGHT}`}
              />
              <path
                d={`M ${
                  PITCH_WIDTH - CORNER_R
                } ${PITCH_HEIGHT} A ${CORNER_R} ${CORNER_R} 0 0 0 ${PITCH_WIDTH} ${
                  PITCH_HEIGHT - CORNER_R
                }`}
              />
            </g>

            {/* Pass arrows (start -> logged destination) */}
            <g data-testid={testId ? `${testId}-arrows` : undefined}>
              {data.arrows.map((arrow) => (
                <line
                  key={arrow.id}
                  data-testid={
                    testId ? `${testId}-arrow-${arrow.id}` : undefined
                  }
                  data-success={arrow.success ? "true" : "false"}
                  x1={arrow.x1}
                  y1={arrow.y1}
                  x2={arrow.x2}
                  y2={arrow.y2}
                  stroke={arrow.success ? SUCCESS_COLOR : FAIL_COLOR}
                  strokeWidth={0.6}
                  strokeOpacity={0.85}
                  strokeLinecap="round"
                  markerEnd={`url(#${
                    arrow.success ? successMarkerId : failMarkerId
                  })`}
                />
              ))}
            </g>

            {/* Start-point markers (no logged destination) */}
            <g data-testid={testId ? `${testId}-markers` : undefined}>
              {data.markers.map((marker) => (
                <circle
                  key={marker.id}
                  data-testid={
                    testId ? `${testId}-marker-${marker.id}` : undefined
                  }
                  data-success={marker.success ? "true" : "false"}
                  cx={marker.x}
                  cy={marker.y}
                  r={1.3}
                  fill={marker.success ? SUCCESS_COLOR : FAIL_COLOR}
                  fillOpacity={0.85}
                  stroke="rgba(255,255,255,0.7)"
                  strokeWidth={0.25}
                />
              ))}
            </g>
          </svg>

          {isEmpty && (
            <div
              className="absolute inset-0 flex items-center justify-center px-4 text-center text-base text-slate-100 bg-slate-900/40"
              data-testid={testId ? `${testId}-empty` : undefined}
            >
              {t(
                "analytics.passMapEmpty",
                "Not enough location data for this player's passes yet.",
              )}
            </div>
          )}
        </div>

        {/* Legend */}
        <div
          className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-slate-200"
          data-testid={testId ? `${testId}-legend` : undefined}
        >
          <span className="flex items-center gap-1.5">
            <span
              className="inline-block h-0.5 w-4 rounded"
              style={{ backgroundColor: SUCCESS_COLOR }}
            />
            {t("analytics.passMapLegendSuccess", "Successful pass")}
          </span>
          <span className="flex items-center gap-1.5">
            <span
              className="inline-block h-0.5 w-4 rounded"
              style={{ backgroundColor: FAIL_COLOR }}
            />
            {t("analytics.passMapLegendUnsuccessful", "Unsuccessful pass")}
          </span>
          <span className="flex items-center gap-1.5">
            <span
              className="inline-block h-2 w-2 rounded-full border border-white/70"
              style={{ backgroundColor: "rgba(255,255,255,0.4)" }}
            />
            {t("analytics.passMapLegendNoDestination", "No logged destination")}
          </span>
        </div>
      </div>

      {/* Attacking-direction note — same raw pitch orientation as the heat
          map (coordinates are not flipped/re-oriented per team side).
          T7 (EPIC-61 brief 15/AC14, client wave 2026-09-19): was
          `text-slate-500`, pre-existing since 2026-07-28 (`ed1fbd3`, not
          this wave's own text) — computed at 3.07:1 against this card's
          slate-800 backdrop, the exact known WCAG AA failure this codebase
          has already shipped twice (`MatchPeriodSelector.tsx`'s own
          "locked decision 6" comment states the same floor). Fixed
          opportunistically here because this line sits inside a file this
          wave already owns and edited (PMA-COCK-214), not because the text
          itself is new — `slate-400` is 5.71:1, comfortably AA. */}
      <div
        className="flex items-center gap-1 text-xs text-slate-200"
        data-testid={testId ? `${testId}-direction-note` : undefined}
      >
        <ArrowRight className="h-3 w-3" />
        {t(
          "analytics.passMapDirectionNote",
          "Same pitch orientation as the heat map",
        )}
      </div>
    </div>
  );
};

export default PassMapPitch;
